/**
 * Centralized locale configuration
 */
export const LOCALE_CONFIG = {
  DEFAULT_SOURCE_LOCALE: 'sv-SE',
  SUPPORTED_LOCALES: ['sv-SE', 'en-US', 'nb-NO', 'da-DK'] as const,
} as const;

export type SupportedLocale = typeof LOCALE_CONFIG.SUPPORTED_LOCALES[number];

/**
 * Get the default source locale for guides
 */
export const getDefaultSourceLocale = (): SupportedLocale => {
  return LOCALE_CONFIG.DEFAULT_SOURCE_LOCALE;
};

/**
 * Check if a locale is supported
 */
export const isSupportedLocale = (locale: string): locale is SupportedLocale => {
  return (LOCALE_CONFIG.SUPPORTED_LOCALES as readonly string[]).includes(locale);
};

/**
 * Get locale from browser or return default
 */
export const getBrowserLocale = (): SupportedLocale => {
  const browserLocale = navigator.language;
  
  // Try exact match first
  if (isSupportedLocale(browserLocale)) {
    return browserLocale;
  }
  
  // Try language code only (e.g. 'sv' from 'sv-FI')
  const languageCode = browserLocale.split('-')[0];
  const matchingLocale = LOCALE_CONFIG.SUPPORTED_LOCALES.find(locale => 
    locale.startsWith(languageCode)
  );
  
  return matchingLocale || getDefaultSourceLocale();
};

/**
 * Map i18n language code to full locale
 */
export const mapI18nLanguageToLocale = (language: string): SupportedLocale => {
  // Map common language codes to full locales
  const languageToLocaleMap: Record<string, SupportedLocale> = {
    'sv': 'sv-SE',
    'en': 'en-US',
    'nb': 'nb-NO',
    'no': 'nb-NO',
    'da': 'da-DK',
  };
  
  // Check if it's already a full locale
  if (isSupportedLocale(language)) {
    return language;
  }
  
  // Try to map from short code
  const mapped = languageToLocaleMap[language];
  if (mapped) {
    return mapped;
  }
  
  // Fallback to browser locale or default
  return getBrowserLocale();
};